import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getProducts } from "../api";
import { Product } from "../types";
import ProductCard from "../components/ProductCard";
import { cloudinaryOptimized } from "../utils/cloudinary";

const Home: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProducts()
      .then((data) => setProducts(data.slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = products[0];

  return (
    <div className="bg-ks-bg min-h-screen">
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="
          relative -mt-[104px] min-h-screen
          bg-ks-bg bg-cover bg-center bg-no-repeat
          flex items-end justify-start
          px-6 md:px-16 lg:px-24 pb-16 md:pb-24
        "
        style={
          featured?.hero_image_url
            ? {
                backgroundImage: `url(${cloudinaryOptimized(featured.hero_image_url, { width: 1920 })})`,
              }
            : undefined
        }
      >
        {/* Dark overlay — layered so the text zone stays readable no matter
            how bright the underlying product photo is (e.g. white backdrop
            shots), instead of relying on a single thin top-to-bottom fade. */}
        <div className="absolute inset-0 bg-gradient-to-t from-ks-bg from-10% via-ks-bg/70 via-40% to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-ks-bg/80 via-ks-bg/20 via-40% to-transparent" />

        <div className="relative z-10 max-w-2xl animate-slide-up">
          <p className="font-saira uppercase tracking-[0.3em] text-ks-accent text-sm mb-3">
            Drop 01 — Origins
          </p>
          <h1
            className="font-saira uppercase text-ks-text leading-none mb-6"
            style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
          >
            Engineered
            <br />
            for the Few
          </h1>
          <p className="font-montserrat text-ks-secondary text-sm md:text-base max-w-md mb-8 leading-relaxed">
            A jersey built on Egyptian mythology — scarab emblem, lotus motif,
            hieroglyphic trim. Precision construction, durable materials,
            limited run.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to={featured ? `/products/${featured.id}` : "/"}
              className="inline-block bg-ks-text text-ks-bg font-saira uppercase tracking-widest text-sm px-8 py-4 hover:bg-ks-warm transition-colors"
            >
              Shop Now
            </Link>
            <a
              href="#featured"
              className="inline-block border border-ks-border text-ks-text font-saira uppercase tracking-widest text-sm px-8 py-4 hover:bg-ks-card transition-colors"
            >
              Explore
            </a>
          </div>
        </div>
      </section>

      {/* ─── Featured Products ─────────────────────────────────────────────── */}
      <section id="featured" className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <div className="mb-12">
          <p className="font-saira uppercase tracking-[0.3em] text-ks-muted text-xs mb-2">
            Selected Pieces
          </p>
          <h2
            className="font-saira uppercase text-ks-text"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
          >
            Featured
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-ks-card mb-4" />
                  <div className="h-4 bg-ks-card mb-2 w-3/4" />
                  <div className="h-3 bg-ks-card w-1/3" />
                </div>
              ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-saira uppercase tracking-widest text-ks-muted text-xl mb-2">
              Collection Coming Soon
            </p>
            <p className="font-montserrat text-ks-muted text-sm">
              Subscribe to get notified on launch day.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* ─── Brand Statement ───────────────────────────────────────────────── */}
      <section className="border-y border-ks-border py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-saira uppercase tracking-[0.3em] text-ks-muted text-xs mb-4">
                The Kit Studio Way
              </p>
              <h2
                className="font-saira uppercase text-ks-text leading-tight mb-6"
                style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
              >
                Built to Last.
                <br />
                Made to Move.
              </h2>
              <p className="font-montserrat text-ks-secondary text-sm leading-relaxed max-w-md mb-8">
                We don't follow trends. We engineer pieces for the long run,
                designed with intention, constructed to precision, and cut for
                those who live in motion.
              </p>
              <Link
                to={featured ? `/products/${featured.id}` : "/"}
                className="font-saira uppercase tracking-widest text-xs text-ks-accent hover:text-ks-text transition-colors flex items-center gap-2"
              >
                Explore the Collection
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-ks-surface border border-ks-border p-8 flex flex-col gap-3">
                <span className="font-saira text-ks-text text-4xl">01</span>
                <h3 className="font-saira uppercase tracking-wide text-ks-text text-lg">
                  Precision Cut
                </h3>
                <p className="font-montserrat text-ks-secondary text-xs leading-relaxed">
                  Every seam, every stitch. Placed with purpose.
                </p>
              </div>
              <div className="bg-ks-surface border border-ks-border p-8 flex flex-col gap-3 mt-8">
                <span className="font-saira text-ks-text text-4xl">02</span>
                <h3 className="font-saira uppercase tracking-wide text-ks-text text-lg">
                  Premium Fabric
                </h3>
                <p className="font-montserrat text-ks-secondary text-xs leading-relaxed">
                  Materials chosen for performance and longevity.
                </p>
              </div>
              <div className="bg-ks-surface border border-ks-border p-8 flex flex-col gap-3">
                <span className="font-saira text-ks-text text-4xl">03</span>
                <h3 className="font-saira uppercase tracking-wide text-ks-text text-lg">
                  Limited Runs
                </h3>
                <p className="font-montserrat text-ks-secondary text-xs leading-relaxed">
                  Each drop is finite. When it's gone, it's gone.
                </p>
              </div>
              <div className="bg-ks-surface border border-ks-border p-8 flex flex-col gap-3 mt-8">
                <span className="font-saira text-ks-text text-4xl">04</span>
                <h3 className="font-saira uppercase tracking-wide text-ks-text text-lg">
                  Timeless Form
                </h3>
                <p className="font-montserrat text-ks-secondary text-xs leading-relaxed">
                  No season, no trend. Just enduring design.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
